import React, { useRef, useState } from 'react';
import { Button, Modal, Select, Table, Tag, message } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import SkuDetails from '../../components/EansDetails/SkuDetails';
import { useListingCompanies } from '../../hooks/useCompanies';
import { request, upload } from '../../utils/axios-utils';

const UPDATE_EAN_URL = 'https://master.dwemz3sd3p6x.amplifyapp.com';
const GUIDELINE_URL = 'https://master.d2aa62ygg90qvn.amplifyapp.com';

const ACTION_LABEL = {
    eanUpdated: 'Barcode updated',
    csvApplied: 'CSV applied',
    resolved: 'Marked resolved',
    markOldAsSupport: 'Old SKU marked as support',
    markNewAsSupport: 'New SKU marked as support',
    markAsSupport: 'Marked as support',
};

const fetchCases = ({ status, type, companyCode }) =>
    request({
        url: '/skuIssues',
        method: 'GET',
        params: { status, type: type || undefined, companyCode: companyCode || undefined },
    });

function when(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString();
}

function downloadText(filename, text) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    link.download = filename;
    link.click();
}

function duplicatePair(row) {
    return {
        _id: row._id,
        oldSkuId: row.oldSkuId,
        newSkuId: row.newSkuId || row.skuId,
        oldSkuName: row.oldSkuName || '',
        newSkuName: row.newSkuName || row.skuName || '',
    };
}

function confirmMarkSupport({ label, name, onOk }) {
    const skuLabel = name ? `${label} SKU ${name}` : `${label} SKU`;
    Modal.confirm({
        title: 'Mark this SKU as support?',
        content: `${skuLabel} will be marked as support, and this duplicate case will be closed.`,
        okText: 'Mark as support',
        cancelText: 'Cancel',
        zIndex: 2000,
        onOk: async () => {
            const ok = await onOk();
            if (!ok) throw new Error('Could not mark SKU as support');
        },
    });
}

function ComparePane({ label, name, skuId, onMarkSupport }) {
    return (
        <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>{name ? `${label} · ${name}` : label}</div>
                {onMarkSupport && skuId ? (
                    <Button size="small" onClick={() => confirmMarkSupport({ label, name, onOk: onMarkSupport })}>
                        Mark this as support
                    </Button>
                ) : null}
            </div>
            {skuId ? (
                <SkuDetails embedded open skuId={skuId} selectedSKU={{ _id: skuId }} onCancel={() => { }} />
            ) : (
                <div>No SKU on this side.</div>
            )}
        </div>
    );
}
function userEmail() {
    try {
        const fromUser = JSON.parse(localStorage.getItem('user') || '{}')?.email;
        if (fromUser) return fromUser;
        const encoded = localStorage.getItem('email');
        return encoded ? atob(encoded) : '';
    } catch {
        return '';
    }
}

function SkuLinks({ skuId, skuName, label, onView }) {
    if (!skuId && !skuName) return null;
    return (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            {label ? <span>{label}</span> : null}
            <Button size="small" disabled={!skuId} onClick={() => onView(skuId)}>
                View SKU details
            </Button>
            <Button
                size="small"
                icon={<FileTextOutlined />}
                title="Open guideline"
                disabled={!skuName}
                href={skuName ? `${GUIDELINE_URL}?skuName=${encodeURIComponent(skuName)}` : undefined}
                target="_blank"
            />
        </span>
    );
}

export default function SkuIssues() {
    const queryClient = useQueryClient();
    const [status, setStatus] = useState('open');
    const [type, setType] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [applyPlan, setApplyPlan] = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [applying, setApplying] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedSkuId, setSelectedSkuId] = useState(null);
    const [selectedPair, setSelectedPair] = useState(null);
    const fileRef = useRef(null);
    const { data: companyCodeList } = useListingCompanies();
    const { data, isFetching } = useQuery(['skuIssues', status, type, companyCode], () => fetchCases({ status, type, companyCode }), {
        select: res => res?.data,
    });
    const resolve = useMutation(({ id, action }) => request({ url: `/skuIssues/${id}/resolve`, method: 'POST', data: { action } }), {
        onSuccess: () => queryClient.invalidateQueries('skuIssues'),
        onError: () => message.error('Could not update the case'),
    });

    const markSupport = async (row, which) => {
        const email = userEmail();
        const skuId = which === 'old' ? row.oldSkuId : (row.newSkuId || row.skuId);
        if (!email || !skuId) {
            message.error('Missing user email or SKU');
            return false;
        }
        try {
            await request({
                url: '/requests/duplicateMarkAsSupport',
                method: 'GET',
                params: { emailId: email, skuId },
            });
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data || '';
            const alreadySupport = /already been marked as support/i.test(String(detail));
            if (!alreadySupport) {
                message.error(detail || 'Could not mark SKU as support');
                return false;
            }
        }
        try {
            await request({
                url: `/skuIssues/${row._id}/resolve`,
                method: 'POST',
                data: { action: which === 'old' ? 'markOldAsSupport' : 'markNewAsSupport' },
            });
        } catch (error) {
            if (error?.response?.status !== 404) {
                message.error(error?.response?.data?.message || 'Could not update the case');
                return false;
            }
        }
        queryClient.invalidateQueries('skuIssues');
        setSelectedPair(null);
        return true;
    };

    const applyCsvFile = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!companyCode) {
            message.error('Select a client first');
            return;
        }
        if (!file.name.toLowerCase().endsWith('.csv')) {
            message.error('Upload a csv file');
            return;
        }
        setApplying(true);
        try {
            const presign = await request({
                url: '/skuIssues/csvUploadUrl',
                method: 'GET',
                params: { companyCode, fname: file.name },
            });
            const { url, key } = presign.data || {};
            await upload({ 'Content-Type': file.type || 'text/csv' }, { url, method: 'PUT', data: file });
            const preview = await request({
                url: '/skuIssues/previewCsv',
                method: 'POST',
                data: { companyCode, key, fileName: file.name },
            });
            setApplyPlan({ fileName: file.name, companyCode, key, ...preview.data });
        } catch (error) {
            message.error(error?.response?.data?.message || error?.response?.data || 'Could not read CSV');
        } finally {
            setApplying(false);
        }
    };

    const confirmApply = async () => {
        if (!applyPlan) return;
        setConfirming(true);
        try {
            const applied = await request({
                url: '/skuIssues/applyCsv',
                method: 'POST',
                data: { companyCode: applyPlan.companyCode, key: applyPlan.key, fileName: applyPlan.fileName },
            });
            setApplyResult({ fileName: applyPlan.fileName, ...applied.data });
            setApplyPlan(null);
            queryClient.invalidateQueries('skuIssues');
        } catch (error) {
            message.error(error?.response?.data?.message || error?.response?.data || 'Could not apply CSV');
        } finally {
            setConfirming(false);
        }
    };

    const exportData = async () => {
        setExporting(true);
        try {
            const res = await request({
                url: '/skuIssues/export',
                method: 'GET',
                params: { status, type: type || undefined, companyCode: companyCode || undefined },
                responseType: 'blob',
            });
            const parts = ['sku-issues', status, type, companyCode].filter(Boolean);
            downloadText(`${parts.join('-')}.csv`, res.data);
        } catch (error) {
            let detail = error?.response?.data?.message;
            if (error?.response?.data instanceof Blob) {
                try {
                    detail = JSON.parse(await error.response.data.text()).message;
                } catch {
                    detail = '';
                }
            }
            message.error(detail || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const counts = data?.counts || { missingBarcode: 0, duplicateSku: 0 };
    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            render: value => (value === 'duplicateSku' ? <Tag color="orange">Duplicate SKU</Tag> : <Tag color="red">Missing barcode</Tag>),
        },
        { title: 'Barcode', dataIndex: 'barcode' },
        { title: 'Client', dataIndex: 'companyCode' },
        { title: 'Order', dataIndex: 'orderName' },
        {
            title: 'SKU',
            render: (_, row) =>
                row.type === 'duplicateSku'
                    ? `${row.oldSkuName || 'old'} → ${row.newSkuName || row.skuName || 'new'}`
                    : row.skuName || '—',
        },
        { title: 'Match', dataIndex: 'duplicateType', render: value => value || '—' },
        { title: 'Raised', dataIndex: 'raisedAt', render: when },
        {
            title: '',
            render: (_, row) => {
                const skus =
                    row.type === 'duplicateSku'
                        ? [
                            { skuId: row.oldSkuId, skuName: row.oldSkuName, label: 'Old' },
                            { skuId: row.newSkuId || row.skuId, skuName: row.newSkuName || row.skuName, label: 'New' },
                        ]
                        : [{ skuId: row.skuId, skuName: row.skuName || row.barcode }];
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {skus.map(sku => (
                            <SkuLinks
                                key={sku.label || sku.skuId}
                                {...sku}
                                onView={row.type === 'duplicateSku' ? () => setSelectedPair(duplicatePair(row)) : setSelectedSkuId}
                            />
                        ))}
                    </div>
                );
            },
        },
        ...(status === 'resolved'
            ? [
                { title: 'Resolved', dataIndex: 'resolvedAt', render: when },
                { title: 'Action', dataIndex: 'action', render: value => ACTION_LABEL[value] || value || '—' },
            ]
            : []),
        {
            title: '',
            render: (_, row) => {
                if (status !== 'open') return null;
                if (row.type === 'missingBarcode') {
                    return (
                        <>
                            <Button size="small" href={`${UPDATE_EAN_URL}?id=${row.inventoryId}`} target="_blank">
                                Update barcode
                            </Button>{' '}
                            <Button size="small" onClick={() => resolve.mutate({ id: row._id, action: 'resolved' })}>
                                Mark resolved
                            </Button>
                        </>
                    );
                }
                return (
                    <>
                        <Button size="small" onClick={() => markSupport(row, 'old')}>
                            Mark old as support
                        </Button>{' '}
                        <Button size="small" onClick={() => markSupport(row, 'new')}>
                            Mark new as support
                        </Button>
                    </>
                );
            },
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>Missing barcodes: {counts.missingBarcode}</span>
                <span style={{ fontWeight: 600 }}>Duplicate SKUs: {counts.duplicateSku}</span>
                <Select
                    size="small"
                    value={type}
                    onChange={setType}
                    style={{ width: 180 }}
                    options={[
                        { value: '', label: 'All types' },
                        { value: 'missingBarcode', label: 'Missing barcodes' },
                        { value: 'duplicateSku', label: 'Duplicate SKUs' },
                    ]}
                />
                <Select
                    showSearch
                    size="small"
                    placeholder="All clients"
                    style={{ width: 180 }}
                    value={companyCode}
                    onChange={value => setCompanyCode(value || '')}
                    optionFilterProp="children"
                >
                    <Select.Option value="">All clients</Select.Option>
                    {(companyCodeList || []).map(company => (
                        <Select.Option key={company._id} value={company.code}>
                            {company.code}
                        </Select.Option>
                    ))}
                </Select>
                <Button size="small" loading={exporting} onClick={exportData}>
                    Export data
                </Button>
                <Button size="small" type={status === 'resolved' ? 'primary' : 'default'} onClick={() => setStatus(status === 'open' ? 'resolved' : 'open')}>
                    {status === 'open' ? 'View resolved cases' : 'View open cases'}
                </Button>
                <Button
                    size="small"
                    loading={applying}
                    disabled={!companyCode}
                    title={companyCode ? 'Apply one CSV to open missing barcodes for this client' : 'Select a client first'}
                    onClick={() => fileRef.current?.click()}
                >
                    Apply CSV
                </Button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={applyCsvFile} />
            </div>
            <Modal
                title="Confirm CSV apply"
                open={!!applyPlan}
                width={720}
                onCancel={() => !confirming && setApplyPlan(null)}
                footer={[
                    applyPlan?.appliedCsv ? (
                        <Button key="applied" onClick={() => downloadText(`applied-${applyPlan.fileName}`, applyPlan.appliedCsv)}>
                            Download applied
                        </Button>
                    ) : null,
                    applyPlan?.leftoverCsv ? (
                        <Button key="leftover" onClick={() => downloadText(`leftover-${applyPlan.fileName}`, applyPlan.leftoverCsv)}>
                            Download leftover
                        </Button>
                    ) : null,
                    <Button key="cancel" disabled={confirming} onClick={() => setApplyPlan(null)}>
                        Cancel
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        loading={confirming}
                        disabled={!applyPlan?.toApply?.length && !applyPlan?.alreadyFilled?.length}
                        onClick={confirmApply}
                    >
                        Confirm apply
                    </Button>,
                ].filter(Boolean)}
            >
                {applyPlan && (
                    <div>
                        <p>
                            {applyPlan.toApply.length} row(s) will be added to {applyPlan.orders} order(s). Nothing is written until you confirm.
                        </p>
                        {!!applyPlan.toApply.length && (
                            <Table
                                size="small"
                                rowKey={(row, index) => `${row.barcode}-${row.orderName}-${index}`}
                                dataSource={applyPlan.toApply}
                                pagination={{ pageSize: 8 }}
                                columns={[
                                    { title: 'Barcode', dataIndex: 'barcode' },
                                    { title: 'Order', dataIndex: 'orderName' },
                                    { title: 'SKU', dataIndex: 'skuName', render: value => value || '—' },
                                ]}
                            />
                        )}
                        {!!applyPlan.alreadyFilled?.length && (
                            <p>{applyPlan.alreadyFilled.length} case(s) already have CSV data and will be closed.</p>
                        )}
                        {applyPlan.leftoverCount > 0 && (
                            <p>{applyPlan.leftoverCount} row(s) will not be applied. Download the leftover file to see them.</p>
                        )}
                        {!!applyPlan.ambiguous?.length && (
                            <p>
                                Ambiguous barcodes (left open):{' '}
                                {applyPlan.ambiguous.map(item => `${item.barcode} (${item.orderNames.join(', ')})`).join('; ')}
                            </p>
                        )}
                        {!!applyPlan.notOnArticle?.length && (
                            <p>
                                Matched a case but not the article:{' '}
                                {applyPlan.notOnArticle.map(item => `${item.barcode} (${item.orderName || 'order'})`).join('; ')}
                            </p>
                        )}
                    </div>
                )}
            </Modal>
            <Modal
                title="CSV apply result"
                open={!!applyResult}
                onCancel={() => setApplyResult(null)}
                footer={[
                    applyResult?.appliedCsv ? (
                        <Button key="applied" onClick={() => downloadText(`applied-${applyResult.fileName}`, applyResult.appliedCsv)}>
                            Download applied
                        </Button>
                    ) : null,
                    applyResult?.leftoverCsv ? (
                        <Button key="leftover" onClick={() => downloadText(`leftover-${applyResult.fileName}`, applyResult.leftoverCsv)}>
                            Download leftover
                        </Button>
                    ) : null,
                    <Button key="close" type="primary" onClick={() => setApplyResult(null)}>
                        Close
                    </Button>,
                ].filter(Boolean)}
            >
                {applyResult && (
                    <div>
                        <p>Applied {applyResult.applied} row(s) across {applyResult.orders} order(s).</p>
                        {applyResult.alreadyFilled > 0 && <p>{applyResult.alreadyFilled} already had CSV data and were closed.</p>}
                        {applyResult.leftoverCount > 0 && <p>{applyResult.leftoverCount} row(s) did not match a single open case. Download the leftover file.</p>}
                        {!!applyResult.ambiguous?.length && (
                            <p>
                                Ambiguous barcodes (left open):{' '}
                                {applyResult.ambiguous.map(item => `${item.barcode} (${item.orderNames.join(', ')})`).join('; ')}
                            </p>
                        )}
                        {!!applyResult.notOnArticle?.length && (
                            <p>
                                Matched a case but not the article:{' '}
                                {applyResult.notOnArticle.map(item => `${item.barcode} (${item.orderName || 'order'})`).join('; ')}
                            </p>
                        )}
                        {!!applyResult.failedOrders?.length && (
                            <p>Failed orders: {applyResult.failedOrders.map(item => `${item.orderName}: ${item.error}`).join('; ')}</p>
                        )}
                    </div>
                )}
            </Modal>
            <Table
                rowKey="_id"
                size="small"
                loading={isFetching}
                dataSource={data?.rows || []}
                columns={columns}
                pagination={{ defaultPageSize: 50, showSizeChanger: true }}
            />
            {selectedSkuId && (
                <SkuDetails
                    open
                    skuId={selectedSkuId}
                    selectedSKU={{ _id: selectedSkuId }}
                    onCancel={() => setSelectedSkuId(null)}
                />
            )}
            {selectedPair && (
                <Modal
                    title="Duplicate SKUs"
                    open
                    width="96vw"
                    style={{ top: 16 }}
                    bodyStyle={{ background: '#f5f6f8' }}
                    onCancel={() => setSelectedPair(null)}
                    footer={<Button onClick={() => setSelectedPair(null)}>Close</Button>}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <ComparePane
                            label="Old"
                            name={selectedPair.oldSkuName}
                            skuId={selectedPair.oldSkuId}
                            onMarkSupport={status === 'open' ? () => markSupport(selectedPair, 'old') : null}
                        />
                        <ComparePane
                            label="New"
                            name={selectedPair.newSkuName}
                            skuId={selectedPair.newSkuId}
                            onMarkSupport={status === 'open' ? () => markSupport(selectedPair, 'new') : null}
                        />
                    </div>
                </Modal>
            )}
        </div>
    );
}
